// src/pages/ResetPassword.jsx
import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  Container,
  useToast,
  InputGroup,
  InputRightElement,
  IconButton,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../config/supabaseClient';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Verificar se existe um token de recuperação na URL
    const hashParams = new URLSearchParams(location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    
    const validateToken = async () => {
      if (accessToken) {
        // Se temos um token, vamos verificar se é válido
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          
          if (error) {
            throw error;
          }
          
          if (data.session) {
            setTokenValid(true);
          }
        } catch (err) {
          console.error('Erro ao validar token:', err);
          setError('Token de recuperação inválido ou expirado.');
        }
      } else {
        setError('Nenhum token de recuperação fornecido.');
      }
    };
    
    validateToken();
  }, [location.hash]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }
    
    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: 'Senha alterada com sucesso!',
        description: 'Sua senha foi atualizada. Você já pode fazer login com sua nova senha.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      // Redirecionar para a página de login
      navigate('/login');
    } catch (err) {
      console.error('Erro ao resetar senha:', err);
      setError(err.message || 'Ocorreu um erro ao resetar sua senha.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxW="md" py={12}>
      <VStack spacing={8} align="stretch">
        <Heading textAlign="center">Redefinir Senha</Heading>
        
        {error && (
          <Text color="red.500" textAlign="center">
            {error}
          </Text>
        )}
        
        {!tokenValid ? (
          <VStack spacing={4}>
            <Text>O link de recuperação é inválido ou expirou.</Text>
            <Button
              as="a"
              href="/forgot-password"
              colorScheme="blue"
              width="full"
            >
              Solicitar novo link
            </Button>
          </VStack>
        ) : (
          <Box as="form" onSubmit={handleResetPassword}>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Nova Senha</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Digite sua nova senha"
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={showPassword ? 'Esconder senha' : 'Mostrar senha'}
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={() => setShowPassword(!showPassword)}
                      variant="ghost"
                      size="sm"
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>Confirmar Nova Senha</FormLabel>
                <InputGroup>
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirme sua nova senha"
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={showConfirmPassword ? 'Esconder senha' : 'Mostrar senha'}
                      icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      variant="ghost"
                      size="sm"
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              
              <Button
                type="submit"
                colorScheme="blue"
                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                color="white"
                size="lg"
                isLoading={loading}
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'md',
                }}
              >
                Redefinir Senha
              </Button>
            </VStack>
          </Box>
        )}
      </VStack>
    </Container>
  );
};

export default ResetPassword;